/**
 * Defaults and bounds for sender follow-ups when a digital e-card stays unopened (PBI #1160).
 * Mirrors ScheduledSmiles.Api.Constants.UnopenedCardFollowUpConstants.
 */

/** Default hours after send before the first unopened follow-up email. */
export const DEFAULT_FOLLOW_UP_1_AFTER_HOURS = 24;

/** Default hours after send before the second unopened follow-up email. */
export const DEFAULT_FOLLOW_UP_2_AFTER_HOURS = 120;

/** Minimum hours for the first follow-up delay. */
export const MIN_FOLLOW_UP_1_AFTER_HOURS = 1;

/** Maximum hours for the first follow-up delay (7 days). */
export const MAX_FOLLOW_UP_1_AFTER_HOURS = 168;

/** Minimum hours for the second follow-up delay (UI bound; must also exceed FU1 when both enabled). */
export const MIN_FOLLOW_UP_2_AFTER_HOURS = 2;

/** Maximum hours for the second follow-up delay (30 days). */
export const MAX_FOLLOW_UP_2_AFTER_HOURS = 720;

/**
 * After this many days from send, Overview banners / contact pills / "Not opened yet"
 * urgency no longer apply (occasion is stale).
 */
export const WARNING_EXPIRES_AFTER_DAYS = 30;

/**
 * Seconds the public card SPA waits after a successful load before POSTing
 * confirm-open (idempotent second writer; GET card-view already records open).
 */
export const CONFIRM_OPEN_DWELL_SECONDS = 2;

/** Locked UI label for Unknown open status tooltip. */
export const UNKNOWN_OPEN_STATUS_TOOLTIP =
  'Open status was not tracked for this card.';

/** Locked UI label for contact-list urgency pill. */
export const CARD_NOT_OPENED_PILL_LABEL = 'Card not opened';

/** Locked UI label for Overview / pill dismiss action. */
export const UNOPENED_WARNING_DISMISS_LABEL = 'Dismiss';

/**
 * Title for the always-visible unopened e-card list under the E-Card service row
 * on Contacts (outside the collapsed history accordion).
 */
export const UNOPENED_ECARD_SECTION_TITLE = 'Not opened yet';

/**
 * Helper copy for the Contacts / Scheduled Items unopened warning section.
 */
export const UNOPENED_ECARD_SECTION_HINT =
  'We have not seen these e-cards opened. A quick check-in can help — including asking them to check spam.';

/**
 * Window event name fired when unopened-warning eligibility may have changed
 * (dismiss, successful Send Now / Resend) so nav badges and contact-list pills
 * can refresh without prop drilling.
 */
export const UNOPENED_WARNINGS_CHANGED_EVENT = 'unopened-warnings:changed';

/**
 * Notifies nav / Contacts / Overview listeners that unopened e-card warning
 * counts may have changed (PBI #1160).
 */
export function notifyUnopenedWarningsChanged(): void {
  window.dispatchEvent(new CustomEvent(UNOPENED_WARNINGS_CHANGED_EVENT));
}

/**
 * Status-filter chip value for unopened digital e-cards on Scheduled Items.
 * Not a {@link ScheduledItemStatus} — filters by open-tracking state.
 */
export const UNOPENED_STATUS_FILTER_VALUE = 'NotOpenedYet';
